import React, { useState } from 'react';
import { CalendarRange } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useAiJob } from '../../hooks/useAiJob';
import { worldTabs, tabLabel } from './worldTabs';
import {
  Box, Frame, Row, Button, Muted, ErrorBox,
} from './adminStyles';

const MIN_SCENARIOS = 2;

/** One scenario's course: its status, and run again against the stored reading (its effect verdict follows it). */
function WorldRow({ instanceId, runId, tab, locked, reload }) {
  const { t } = useTranslation();
  const sim = useAiJob({ type: 'future_reimagined_v2.simulate', dedupeKey: `future_reimagined_v2.simulate:${runId}:${tab.worldId}` });
  const [error, setError] = useState(null);
  const status = tab.timeline.status;
  const running = ['queued', 'running'].includes(sim.status) || status === 'running';
  const act = async (input) => {
    setError(null);
    try { await sim.start({ instanceId, ...input }); } catch (e) { setError(e.message); } finally { reload(); }
  };
  return (
    <Frame>
      <Row>
        <strong title={tab.title}>{tabLabel(tab, 48)}</strong>
        <small>{t(`futureV2.worldAdmin.status.${running ? 'running' : status}`)}{tab.timeline.stale ? ` · ${t('futureV2.scenario.stale')}` : ''}</small>
      </Row>
      <Row>
        <Button type="button" onClick={() => act({ worldId: tab.worldId })} disabled={locked || running || tab.timeline.stale}>
          <CalendarRange size={14} aria-hidden />{t('futureV2.worldAdmin.rerun')}
        </Button>
      </Row>
      {(error || sim.status === 'failed' || status === 'failed') && (
        <ErrorBox role="alert">{t('futureV2.course.failed', { message: error || String(sim.error || tab.timeline.error || '') })}</ErrorBox>
      )}
    </Frame>
  );
}

/**
 * The facilitator's scenarios (#1474, copied from v1's WorldRuns, #1062): which source is in use
 * and how many it gives, the reading's status, and per scenario a re-run against the stored
 * reading, its effect verdict with it. Reading them all and imagining a course in each is the
 * module's admin action "simulate" in the section's action shelf
 * (specs/facilitator-console.md); this shows what it produced.
 */
export default function WorldRuns({ instanceId, state, locked, reload }) {
  const { t } = useTranslation();
  const scenarios = state.scenarios || { list: [] };
  const tabs = worldTabs(state.timelines || []);
  const enough = scenarios.list.length >= MIN_SCENARIOS;
  return (
    <Box>
      <h4>{t('futureV2.worldAdmin.title')}</h4>
      <Muted>
        {enough
          ? t('futureV2.worldAdmin.source', { source: t(`futureV2.scenario.sources.${scenarios.source}`), count: scenarios.list.length })
          : t('futureV2.worldAdmin.none', { count: scenarios.list.length, min: MIN_SCENARIOS })}
        {scenarios.placeholder && scenarios.source === 'expert' ? ` ${t('futureV2.scenario.placeholder')}` : ''}
      </Muted>
      {state.reading && <Muted>{t(`futureV2.worldAdmin.readingStatus.${state.reading.status}`)}</Muted>}
      {state.reading?.status === 'failed' && <ErrorBox role="alert">{t('futureV2.reading.failed', { message: state.reading.error || '' })}</ErrorBox>}
      {tabs.map((tab) => <WorldRow key={tab.id} instanceId={instanceId} runId={state.run.id} tab={tab} locked={locked} reload={reload} />)}
    </Box>
  );
}
